import React, { useState, useMemo } from 'react';
import { Search, Trash2, Eye, ChevronLeft, ChevronRight, Users, Plus, UserPlus, Pencil, X, Check } from 'lucide-react';
import RecipientDetailModal from './RecipientDetailModal';
import followupService from '../../services/global/followupService';
import { followupPhoneToRaw, isBrPhoneInput, maskFollowupPhoneInput } from './followupPhone';

const PAGE_SIZE = 20;


const getPhone = (r) => (typeof r === 'string' ? r : r?.phone || '');
const getAddedAt = (r) => (typeof r === 'string' ? null : r?.added_at || null);
const getSource = (r) => (typeof r === 'string' ? null : r?.source || null);
const getScheduleOverride = (r) => (typeof r === 'string' ? null : r?.schedule_override || null);

const calcCurrentDay = (addedAt) => {
  if (!addedAt) return null;
  const added = new Date(addedAt + 'T00:00:00');
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = today - added;
  const diffDays = Math.floor(diffMs / 86400000);
  return diffDays + 1;
};

const RECURRENCE_LABELS = {
  once: 'Uma vez',
  daily: 'Diário',
  weekly: 'Semanal',
  monthly: 'Mensal',
};

/** Inline schedule editor for a recipient */
const InlineScheduleEditor = ({ recipientData, followup, colors, styles, onSave, onCancel }) => {
  const override = getScheduleOverride(recipientData) || followup.schedule || {};
  const [time, setTime] = useState(override.send_time || '09:00');
  const [rec, setRec] = useState(override.recurrence || 'once');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        send_time: time,
        send_date: override.send_date || followup.schedule?.start_date || new Date().toISOString().split('T')[0],
        recurrence: rec,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td colSpan={6} style={{ padding: '8px 14px' }}>
        <div style={{
          display: 'flex', gap: '8px', alignItems: 'center',
          padding: '10px 12px', borderRadius: '8px',
          backgroundColor: `${colors.accentPrimary}05`,
          border: `1px solid ${colors.accentPrimary}20`,
        }}>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={{
              padding: '6px 8px', borderRadius: '6px', fontSize: '12px',
              border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
              color: colors.textPrimary, outline: 'none',
            }}
          />
          <select
            value={rec}
            onChange={(e) => setRec(e.target.value)}
            style={{
              padding: '6px 8px', borderRadius: '6px', fontSize: '12px',
              border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
              color: colors.textPrimary, outline: 'none',
            }}
          >
            <option value="once">Uma vez</option>
            <option value="daily">Diário</option>
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
          </select>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              padding: '5px 10px', borderRadius: '6px', fontSize: '11px',
              backgroundColor: colors.accentPrimary, color: '#fff',
              border: 'none', cursor: 'pointer', opacity: saving ? 0.5 : 1,
            }}
          >
            <Check size={11} /> {saving ? '...' : 'Salvar'}
          </button>
          <button
            onClick={onCancel}
            style={{
              display: 'flex', alignItems: 'center',
              padding: '5px 8px', borderRadius: '6px', fontSize: '11px',
              backgroundColor: 'transparent', color: colors.textSecondary,
              border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
            }}
          >
            <X size={11} />
          </button>
        </div>
      </td>
    </tr>
  );
};

const RecipientsTab = ({ followup, history, colors, styles, onRemoveRecipient, onRefresh, onAddRecipients, onOpenContactPicker, onResetRecipient, onResetDay }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [detailPhone, setDetailPhone] = useState(null);
  const [newNumber, setNewNumber] = useState('');
  const [editingPhone, setEditingPhone] = useState(null);

  const recipients = followup.recipients || [];
  const isScheduledMessage = followup.type === 'scheduled_message';

  const recipientStats = useMemo(() => {
    const stats = {};
    history.forEach(h => {
      if (!stats[h.recipient]) {
        stats[h.recipient] = { daysSet: new Set(), msgsSent: 0, totalMsgs: 0 };
      }
      stats[h.recipient].totalMsgs++;
      if (h.status === 'sent') {
        stats[h.recipient].daysSet.add(h.day);
        stats[h.recipient].msgsSent++;
      }
    });
    return stats;
  }, [history]);

  const filtered = recipients.filter(r => {
    const phone = getPhone(r);
    return !searchTerm || phone.includes(searchTerm);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const handleRemove = (phone) => {
    if (window.confirm(`Remover ${phone} dos destinatários?`)) {
      onRemoveRecipient(phone);
    }
  };

  const handleAddManual = () => {
    // DDD + número ganha o 55; com "+" ou com DDI (12 a 15 dígitos) vale como
    // veio. Antes, +1, Portugal e Argentina davam "Número inválido".
    const normalized = followupPhoneToRaw(newNumber);
    if (!normalized) {
      alert('Número inválido. Informe DDD + número, ou o número de outro país começando com + (ex.: +1 415 555 0123).');
      return;
    }
    onAddRecipients([normalized]);
    setNewNumber('');
  };

  const handleSaveScheduleOverride = async (phone, scheduleData) => {
    try {
      await followupService.scheduleForContact(followup.id, {
        phone,
        ...scheduleData,
      });
      setEditingPhone(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.detail || err.message));
    }
  };

  const totalDays = followup.total_days || 0;
  const globalSchedule = followup.schedule || {};

  return (
    <>
      {/* Add recipients */}
      <div style={{
        ...styles.card, marginBottom: '16px', cursor: 'default',
        display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          {isBrPhoneInput(newNumber) && (
            <span style={{
              position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
              fontSize: '14px', color: colors.textTertiary, fontWeight: 500, pointerEvents: 'none',
            }}>
              +55
            </span>
          )}
          <input
            value={newNumber}
            onChange={e => {
              const masked = maskFollowupPhoneInput(e.target.value);
              if (masked !== null) setNewNumber(masked);
            }}
            onKeyDown={e => { if (e.key === 'Enter') handleAddManual(); }}
            placeholder="(11) 99999-9999"
            title="Número do Brasil com DDD, ou de outro país começando com +"
            style={{ ...styles.input, paddingLeft: isBrPhoneInput(newNumber) ? '46px' : '12px' }}
            maxLength={20}
          />
        </div>
        <button
          onClick={handleAddManual}
          disabled={newNumber.replace(/\D/g, '').length < 10}
          style={{
            ...styles.btn, ...styles.btnPrimary,
            padding: '8px 14px', fontSize: '13px',
            opacity: newNumber.replace(/\D/g, '').length >= 10 ? 1 : 0.5,
          }}
        >
          <Plus size={14} /> Adicionar
        </button>
        {onOpenContactPicker && (
          <button
            onClick={onOpenContactPicker}
            style={{ ...styles.btn, ...styles.btnOutline, padding: '8px 14px', fontSize: '13px' }}
          >
            <UserPlus size={14} /> Buscar contatos
          </button>
        )}
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={16} style={{
            position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
            color: colors.textSecondary,
          }} />
          <input
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
            placeholder="Buscar número..."
            style={{ ...styles.input, paddingLeft: '34px' }}
          />
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px',
        fontSize: '13px', color: colors.textSecondary,
      }}>
        <Users size={14} />
        <span>{recipients.length} destinatário{recipients.length !== 1 ? 's' : ''}</span>
        {searchTerm && <span> ({filtered.length} encontrado{filtered.length !== 1 ? 's' : ''})</span>}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '40px 20px', color: colors.textSecondary, fontSize: '14px',
        }}>
          {recipients.length === 0 ? 'Nenhum destinatário adicionado.' : 'Nenhum resultado encontrado.'}
        </div>
      ) : (
        <div style={{
          backgroundColor: colors.bgPrimary, borderRadius: '12px',
          border: `1px solid ${colors.borderColor}`, overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Número
                </th>
                {isScheduledMessage ? (
                  <>
                    <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                      Origem
                    </th>
                    <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                      Horário
                    </th>
                    <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                      Recorrencia
                    </th>
                  </>
                ) : (
                  <>
                    <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                      Dia atual
                    </th>
                    <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                      Dias recebidos
                    </th>
                  </>
                )}
                <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Msgs enviadas
                </th>
                <th style={{ textAlign: 'right', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(recipientData => {
                const phone = getPhone(recipientData);
                const addedAt = getAddedAt(recipientData);
                const source = getSource(recipientData);
                const scheduleOverride = getScheduleOverride(recipientData);
                const stat = recipientStats[phone];
                const msgsSent = stat ? stat.msgsSent : 0;

                // For challenge_sequence
                const currentDay = calcCurrentDay(addedAt);
                const daysReceived = stat ? stat.daysSet.size : 0;
                const isCompleted = currentDay && currentDay > totalDays;
                const dayLabel = isCompleted ? 'Concluido' : currentDay ? `${currentDay}/${totalDays}` : '-';

                // For scheduled_message
                const effectiveSchedule = scheduleOverride || globalSchedule;
                const effectiveTime = effectiveSchedule.send_time || '09:00';
                const effectiveRecurrence = effectiveSchedule.recurrence || 'once';

                return (
                  <React.Fragment key={phone}>
                    <tr style={{ borderBottom: `1px solid ${colors.borderColor}20` }}>
                      <td style={{ padding: '10px 14px', color: colors.textPrimary, fontFamily: 'monospace', fontSize: '13px' }}>
                        {phone}
                      </td>
                      {isScheduledMessage ? (
                        <>
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            {source === 'inbox' ? (
                              <span style={{
                                padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                                backgroundColor: '#0ea5e920', color: '#0ea5e9',
                              }}>
                                Inbox
                              </span>
                            ) : (
                              <span style={{ fontSize: '11px', color: colors.textTertiary }}>Manual</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary, fontSize: '13px' }}>
                            {effectiveTime}
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary, fontSize: '12px' }}>
                            {RECURRENCE_LABELS[effectiveRecurrence] || effectiveRecurrence}
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: isCompleted ? '#22c55e' : colors.textPrimary, fontWeight: isCompleted ? 600 : 400 }}>
                            {dayLabel}
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary }}>
                            {daysReceived}
                          </td>
                        </>
                      )}
                      <td style={{ padding: '10px 14px', textAlign: 'center', color: colors.textPrimary }}>
                        {msgsSent}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          {isScheduledMessage && (
                            <button
                              onClick={() => setEditingPhone(editingPhone === phone ? null : phone)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '4px',
                                background: 'none', border: `1px solid ${colors.borderColor}`,
                                borderRadius: '6px', padding: '4px 8px', fontSize: '11px',
                                color: colors.accentPrimary, cursor: 'pointer',
                              }}
                              title="Editar agendamento"
                            >
                              <Pencil size={11} />
                            </button>
                          )}
                          <button
                            onClick={() => setDetailPhone(phone)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px',
                              background: 'none', border: `1px solid ${colors.borderColor}`,
                              borderRadius: '6px', padding: '4px 10px', fontSize: '11px',
                              color: colors.textSecondary, cursor: 'pointer',
                            }}
                            title="Ver detalhes"
                          >
                            <Eye size={12} /> Detalhes
                          </button>
                          <button
                            onClick={() => handleRemove(phone)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'none', border: `1px solid #ef444430`,
                              borderRadius: '6px', padding: '4px 8px',
                              color: '#ef4444', cursor: 'pointer',
                            }}
                            title="Remover destinatário"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Inline schedule editor */}
                    {editingPhone === phone && isScheduledMessage && (
                      <InlineScheduleEditor
                        recipientData={recipientData}
                        followup={followup}
                        colors={colors}
                        styles={styles}
                        onSave={(data) => handleSaveScheduleOverride(phone, data)}
                        onCancel={() => setEditingPhone(null)}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '12px', marginTop: '16px',
        }}>
          <button
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === 1 ? 0.4 : 1,
              cursor: currentPage === 1 ? 'default' : 'pointer',
            }}
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <span style={{ fontSize: '13px', color: colors.textSecondary }}>
            {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            style={{
              ...styles.btn, ...styles.btnOutline, padding: '6px 12px', fontSize: '12px',
              opacity: currentPage === totalPages ? 0.4 : 1,
              cursor: currentPage === totalPages ? 'default' : 'pointer',
            }}
          >
            Próximo <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <RecipientDetailModal
        isOpen={detailPhone !== null}
        phone={detailPhone}
        history={history}
        followup={followup}
        onClose={() => setDetailPhone(null)}
        onResetRecipient={async (ph) => { await onResetRecipient(ph); }}
        onResetDay={async (day) => { await onResetDay(day); }}
        onRefresh={onRefresh}
        colors={colors}
      />
    </>
  );
};

export default RecipientsTab;
